import classNames from 'classnames';
import {
  format as d3Format,
  treemap as d3Treemap,
  hierarchy,
  treemapSquarify,
} from 'd3';
import { scaleOrdinal } from 'd3-scale';
import truncate from 'lodash/truncate';
import {
  type CSSProperties,
  Fragment,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  DIMMED_PLOT_RAMP_SIZE,
  getDimmedPlotColor,
  getFormatter,
  getTextWidth,
  isDarkFill,
  truncateTextWithFit,
  useChartTheme,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { PlotTooltip } from '../plot-tooltip';
import styles from './treemap.module.css';

export type TreeMapNode = {
  name: string;
  value: number;
  children?: TreeMapNode[];
  color?: string;
  parentName?: string;
  id?: string;
  share?: number | null;
};

export type TreeMapData = {
  name: string;
  children: TreeMapNode[];
};

type TreeDatum = {
  name: string;
  value?: number;
  color?: string;
  parentName?: string;
  children?: TreeDatum[];
  id?: string;
  share?: number | null;
};

export type BreadcrumbSegment = {
  name: string;
  targetDepth: number;
};

export type TreeMapProps = {
  data: TreeMapData;
  colorPalette?: string[];
  colorOverride?: Record<string, string>;
  onCellClick?: (
    cellId: string,
    cellName: string,
    parentName?: string,
    color?: string
  ) => void;
  onHeaderClick?: (groupId: string, groupName: string, color?: string) => void;
  onBreadcrumbClick?: (targetDepth: number) => void;
  breadcrumbPath?: BreadcrumbSegment[];

  sizeTransform?: 'none' | 'sqrt';
  detailLoading?: boolean;
  drilledGroupId?: string;
} & CommonPlotProps;

// Bias cells toward wider-than-tall rectangles (still squarified): lay out in a
// vertically-stretched space, then compress y back.
const HORIZONTAL_STRETCH = 1.6;
const LABEL_PADDING_X = 14;
const NESTED_OTHER_LABEL = 'Other';
const VALUE_SHARE_SEPARATOR = '  /  ';

const TRAY_HEADER_HEIGHT = 22;
// Cells lay out flush (gap 0) so sibling 1px borders merge into one line; nested
// children are inset by TRAY_EDGE_PADDING so groups read as nested rectangles.
const TRAY_EDGE_PADDING = 5;
const ENCLOSED_INNER_GAP = 0;
// Gap between differently-coloured top-level boxes.
const TOP_LEVEL_GAP = 3;

const ROOT_HEADER_HEIGHT = 22;
const ROOT_EDGE_PADDING = 6;

function getOverrideColor(
  name: string,
  colorOverride: Record<string, string> | undefined
): string | undefined {
  if (colorOverride === undefined) return undefined;
  const key = Object.keys(colorOverride).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  return key !== undefined ? colorOverride[key] : undefined;
}

function isOtherDatum(datum: { id?: string; name?: string }): boolean {
  if (datum.id === '-1') return true;
  return (datum.name ?? '').toLowerCase() === 'other';
}

type DimRect = { x0: number; y0: number; x1: number; y1: number };

function sharedEdgeLength(a: DimRect, b: DimRect): number {
  const maxSep = TOP_LEVEL_GAP + 1;
  const sepX = Math.max(a.x0, b.x0) - Math.min(a.x1, b.x1);
  const sepY = Math.max(a.y0, b.y0) - Math.min(a.y1, b.y1);
  if (sepX >= 0 && sepX <= maxSep && sepY < -1) return -sepY;
  if (sepY >= 0 && sepY <= maxSep && sepX < -1) return -sepX;
  return 0;
}

function assignHoverDimIndices<T extends DimRect>(nodes: T[]): Map<T, number> {
  const ordered = [...nodes].sort((a, b) => a.x0 - b.x0 || a.y0 - b.y0);
  const colors = ordered.map((_, i) => i % DIMMED_PLOT_RAMP_SIZE);

  const paint = (i: number, previous: number): boolean => {
    const node = ordered[i];
    if (node === undefined) return true;
    for (let step = 1; step <= DIMMED_PLOT_RAMP_SIZE; step++) {
      const next = (previous + step) % DIMMED_PLOT_RAMP_SIZE;
      const takenByNeighbour = ordered.some(
        (other, j) =>
          j < i && colors[j] === next && sharedEdgeLength(node, other) > 0
      );
      if (takenByNeighbour) continue;
      colors[i] = next;
      if (paint(i + 1, next)) return true;
    }
    return false;
  };
  paint(0, DIMMED_PLOT_RAMP_SIZE - 1);

  return new Map(ordered.map((node, i) => [node, colors[i] ?? 0]));
}

export const TreeMap = ({
  data,
  loading = false,
  format = FormatType.SI,
  sizeTransform = 'none',
  colorPalette: colorPaletteProp,
  colorOverride,
  onCellClick,
  onHeaderClick,
  onBreadcrumbClick,
  breadcrumbPath,
  detailLoading = false,
  drilledGroupId,
}: TreeMapProps) => {
  const clickable = onCellClick !== undefined;
  const headerClickable = onHeaderClick !== undefined;
  const cursorClassName = (isInteractive: boolean): string | undefined => {
    if (detailLoading) return styles.waiting;
    return isInteractive ? styles.pointer : undefined;
  };
  const { breakdownPalette, theme } = useChartTheme();
  const overridenColors = Object.values(colorOverride ?? {});
  const basePalette = colorPaletteProp?.length
    ? colorPaletteProp
    : breakdownPalette;
  const colorPalette = basePalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const { containerRef, width, height } = useResizeObserver();

  const isPlotSizeValid = width > 0 && height > 0;
  const hasData = Array.isArray(data?.children) && data.children.length > 0;

  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const [hoveredLeafId, setHoveredLeafId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    title: string;
    valueText: string;
  } | null>(null);
  const showTooltipAt = useCallback(
    (x: number, y: number, title: string, valueText: string) => {
      const next = { x, y, title, valueText };
      setTooltip((prev) =>
        prev !== null &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.title === next.title &&
        prev.valueText === next.valueText
          ? prev
          : next
      );
    },
    []
  );
  const showTooltipAtCursor = useCallback(
    (
      event: { clientX: number; clientY: number },
      title: string,
      valueText: string
    ) => showTooltipAt(event.clientX, event.clientY, title, valueText),
    [showTooltipAt]
  );
  const showTooltipAtElement = useCallback(
    (element: Element | null, title: string, valueText: string) => {
      const targetRect = element?.getBoundingClientRect();
      if (!targetRect) return;
      showTooltipAt(
        targetRect.left + targetRect.width / 2,
        targetRect.top,
        title,
        valueText
      );
    },
    [showTooltipAt]
  );
  const hideTooltip = useCallback(() => setTooltip(null), []);
  const clearHover = useCallback(() => {
    setHoveredGroupId(null);
    setHoveredLeafId(null);
    setTooltip(null);
  }, []);
  const rootRef = useRef<HTMLDivElement>(null);
  const isHovering =
    tooltip !== null || hoveredGroupId !== null || hoveredLeafId !== null;
  useEffect(() => {
    if (!isHovering) return;
    const onMove = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node | null)) clearHover();
    };
    document.addEventListener('mousemove', onMove, true);
    return () => document.removeEventListener('mousemove', onMove, true);
  }, [isHovering, clearHover]);
  const displayData: TreeMapData = data;
  const topLevelGroups = displayData.children.filter(
    (c) => Array.isArray(c.children) && c.children.length > 0
  );
  const topLevelGroupCount = topLevelGroups.length;
  const soleGroup = topLevelGroupCount === 1 ? topLevelGroups[0] : undefined;
  const hideGroupHeaders =
    soleGroup !== undefined &&
    drilledGroupId !== undefined &&
    String(soleGroup.id ?? soleGroup.name) === drilledGroupId;
  const revealOnHover = topLevelGroupCount > 0 && !hideGroupHeaders;
  const flatHover = !revealOnHover;

  function toTreeDatum(node: TreeMapNode): TreeDatum {
    const children = Array.isArray(node.children)
      ? node.children.map((child) => toTreeDatum(child))
      : undefined;
    return {
      name: node.name,
      value: typeof node.value === 'number' ? node.value : undefined,
      color: node.color,
      parentName: node.parentName,
      children,
      id: node.id,
      share: node.share,
    };
  }

  const rootDatum: TreeDatum = {
    name: displayData.name,
    children: displayData.children.map((c) => toTreeDatum(c)),
  };

  const root = hierarchy<TreeDatum>(rootDatum)
    .sum((d) =>
      d.children && d.children.length ? 0 : Math.max(0, d.value ?? 0)
    )
    .sort((a, b) => {
      const aIsOther = isOtherDatum(a.data);
      const bIsOther = isOtherDatum(b.data);
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (b.value ?? 0) - (a.value ?? 0);
    });

  if (sizeTransform === 'sqrt') {
    // Per-level sqrt: re-weight each node ∝ √(its aggregate) among siblings so
    // group size doesn't inflate with child count. `.data.value` (raw metric,
    // used for labels) is untouched.
    root.eachBefore((node) => {
      const children = node.children;
      if (children === undefined || children.length === 0) return;
      let sqrtSum = 0;
      for (const child of children) {
        sqrtSum += Math.sqrt(Math.max(0, child.value ?? 0));
      }
      const budget = node.value ?? 0;
      const scale = sqrtSum > 0 ? budget / sqrtSum : 0;
      for (const child of children) {
        // d3 types `value` read-only; re-weight it deliberately.
        (child as { value?: number }).value =
          Math.sqrt(Math.max(0, child.value ?? 0)) * scale;
      }
    });
  }

  const crumbs: BreadcrumbSegment[] = breadcrumbPath ?? [];
  const showCrumbBar = crumbs.length > 0;

  const isGroupNode = (node: {
    children?: unknown[];
    depth: number;
  }): boolean =>
    Array.isArray(node.children) &&
    node.children.length > 0 &&
    node.depth > 0 &&
    !hideGroupHeaders;

  const stretch = HORIZONTAL_STRETCH;
  const treemapRoot = d3Treemap<TreeDatum>()
    .tile(treemapSquarify.ratio(1))
    .size([Math.max(0, width), Math.max(0, height * stretch)])
    .paddingInner((node) => {
      // Differently-coloured cells (top-level groups, or the flat terminal drill)
      // get a wider gap; same-coloured nested cells stay flush (border-delineated).
      if (node.depth === 0 || hideGroupHeaders) return TOP_LEVEL_GAP;
      return ENCLOSED_INNER_GAP;
    })
    .paddingTop((node) => {
      if (node.depth === 0) {
        return showCrumbBar ? ROOT_HEADER_HEIGHT * stretch : 0;
      }
      return isGroupNode(node) ? TRAY_HEADER_HEIGHT * stretch : 0;
    })
    .paddingLeft((node) => {
      if (node.depth === 0) return showCrumbBar ? ROOT_EDGE_PADDING : 0;
      return isGroupNode(node) ? TRAY_EDGE_PADDING : 0;
    })
    .paddingRight((node) => {
      if (node.depth === 0) return showCrumbBar ? ROOT_EDGE_PADDING : 0;
      return isGroupNode(node) ? TRAY_EDGE_PADDING : 0;
    })
    .paddingBottom((node) => {
      if (node.depth === 0) {
        return showCrumbBar ? ROOT_EDGE_PADDING * stretch : 0;
      }
      return isGroupNode(node) ? TRAY_EDGE_PADDING * stretch : 0;
    })
    .round(true)(root);
  treemapRoot.each((n) => {
    n.y0 /= stretch;
    n.y1 /= stretch;
  });

  const topLevelYInset = (TOP_LEVEL_GAP * (1 - 1 / stretch)) / 2;

  const leaves = treemapRoot.leaves();
  // Flush (gap 0) siblings share border lines, so a later-painted neighbour
  // would overdraw half of the pointed cell's ink border — paint it last.
  const renderedLeaves = (() => {
    if (!revealOnHover || hoveredLeafId === null) return leaves;
    const isActive = (l: (typeof leaves)[number]) =>
      String(l.data.id ?? l.data.name) === hoveredLeafId;
    return [...leaves.filter((l) => !isActive(l)), ...leaves.filter(isActive)];
  })();
  const totalRawSum = leaves.reduce((sum, leaf) => {
    const rawValue = typeof leaf.data.value === 'number' ? leaf.data.value : 0;
    return sum + rawValue;
  }, 0);
  const groupsWithChildren = hideGroupHeaders
    ? []
    : treemapRoot
        .descendants()
        .filter(
          (n) =>
            n.depth > 0 && Array.isArray(n.children) && n.children.length > 0
        );

  const dimIndexByTopNode = assignHoverDimIndices(treemapRoot.children ?? []);

  // Colour domain follows the value-sorted layout order (largest first, "Other"
  // last) so colours stay stable regardless of raw input order.
  const sortedTopLevelNames = treemapRoot.children
    ? treemapRoot.children.map((n) => n.data.name)
    : displayData.children.map((c) => c.name);
  const leafNames = leaves.map((l) => l.data.name);

  const colorByLeaf =
    !revealOnHover && topLevelGroupCount <= 1 && leaves.length > 1;
  const colorDomain = colorByLeaf
    ? [...new Set(leafNames)]
    : [...new Set(sortedTopLevelNames)];
  const colorScale = scaleOrdinal<string, string>()
    .domain(colorDomain)
    .range(colorPalette);

  const useSingleColor = colorPalette.length === 1;
  const singleColor = colorPalette[0];

  const resolveColor = (name: string): string => {
    const override = getOverrideColor(name, colorOverride);
    if (override !== undefined) return override;
    if (useSingleColor && singleColor !== undefined) return singleColor;
    return colorScale(name);
  };

  const formatValue = getFormatter(format);
  const formatShare = getFormatter(FormatType.PERCENTAGE);
  // Tiny non-zero shares would read "0%" — surface up to two decimals instead.
  const formatShareDetailed = useCallback(
    (share: number) => {
      const text = formatShare(share);
      return text === '0%' && share > 0 ? d3Format('.2~%')(share) : text;
    },
    [formatShare]
  );

  const toDisplayValue = (rawValue: number): number => {
    if (format === FormatType.PERCENTAGE) {
      return totalRawSum === 0 ? 0 : rawValue / totalRawSum;
    }
    return rawValue;
  };

  const measureBreadcrumb = (text: string): number =>
    getTextWidth({ text, fontSize: 12, fontWeight: 400 }) ?? text.length * 6;
  const breadcrumbHeadLabels = crumbs
    .slice(0, -1)
    .map((c) => truncate(c.name, { length: 50 }));
  const breadcrumbLastLabel = (() => {
    const last = crumbs[crumbs.length - 1];
    if (last === undefined) return '';
    const sepWidth = crumbs.length > 1 ? measureBreadcrumb('   ›   ') : 0;
    const totalWidth = measureBreadcrumb(
      `    ${formatValue(toDisplayValue(totalRawSum))}`
    );
    const prefixWidth =
      breadcrumbHeadLabels.reduce((w, t) => w + measureBreadcrumb(t), 0) +
      (crumbs.length - 1) * sepWidth +
      totalWidth;
    const maxWidth = Math.max(0, width - 16 - prefixWidth);
    return truncateTextWithFit({
      text: last.name,
      maxWidth,
      fontSize: 12,
      fontWeight: 400,
    }).text;
  })();
  const breadcrumbLabels = [...breadcrumbHeadLabels, breadcrumbLastLabel];

  return (
    <div
      ref={rootRef}
      className={classNames(styles.container)}
      data-testid={plotTestId(ReplotChartNames.TreeMap)}
      style={detailLoading ? { cursor: 'wait' } : undefined}
      onMouseLeave={clearHover}
    >
      <div ref={containerRef} className={styles.sizingBox}>
        <PlotLoadWrapper loading={loading} noData={!hasData}>
          <svg width="100%" height="100%">
            {isPlotSizeValid && (
              <g>
                {showCrumbBar && (
                  <g>
                    <rect
                      x={0}
                      y={0}
                      width={width}
                      height={height}
                      className={styles.rootTrayRect}
                    />
                    <text x={8} y={14} className={styles.breadcrumbLabel}>
                      {crumbs.map((seg, i) => {
                        const handleClick = () => {
                          onBreadcrumbClick?.(seg.targetDepth);
                        };
                        const handleKeyDown = (e: KeyboardEvent<Element>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                          }
                        };
                        return (
                          <Fragment key={`breadcrumb-${seg.targetDepth}`}>
                            {i > 0 && (
                              <tspan className={styles.breadcrumbSeparator}>
                                &nbsp;&nbsp;&nbsp;›&nbsp;&nbsp;&nbsp;
                              </tspan>
                            )}
                            <tspan
                              data-testid={`treemap-breadcrumb-${seg.targetDepth}`}
                              onClick={handleClick}
                              onKeyDown={handleKeyDown}
                              role="button"
                              tabIndex={0}
                              className={styles.breadcrumbLink}
                              style={{ cursor: 'pointer' }}
                            >
                              {breadcrumbLabels[i]}
                            </tspan>
                          </Fragment>
                        );
                      })}
                      <tspan className={styles.breadcrumbTotal}>
                        {`    ${formatValue(toDisplayValue(totalRawSum))}`}
                      </tspan>
                    </text>
                  </g>
                )}
                {groupsWithChildren.map((groupNode) => {
                  const groupYInset =
                    groupNode.depth === 1 ? topLevelYInset : 0;
                  const trayTop = groupNode.y0 + groupYInset;
                  const rawW = groupNode.x1 - groupNode.x0;
                  const x = Math.max(0, groupNode.x0);
                  const y = trayTop;
                  const w = Math.min(width, groupNode.x1) - x;
                  const h = Math.max(
                    0,
                    groupNode.y1 - groupNode.y0 - 2 * groupYInset
                  );
                  if (rawW <= 0 || h <= 0) return null;

                  const title = String(groupNode.data.name ?? '');
                  const groupTotal = groupNode
                    .leaves()
                    .reduce(
                      (s, l) =>
                        s +
                        (typeof l.data.value === 'number' ? l.data.value : 0),
                      0
                    );
                  const groupShare = groupNode
                    .leaves()
                    .reduce<number | null>(
                      (s, l) =>
                        s === null || typeof l.data.share !== 'number'
                          ? null
                          : s + l.data.share,
                      0
                    );
                  const valueLabel = formatValue(toDisplayValue(groupTotal));
                  const headerLabel = truncateTextWithFit({
                    text: title,
                    maxWidth: Math.max(0, w - LABEL_PADDING_X),
                    fontSize: 12,
                    fontWeight: 401,
                  });
                  const headerLabelShown =
                    w > 40 && h >= 16 && headerLabel.fitted;
                  const headerTooltipShare =
                    groupShare ??
                    (totalRawSum > 0 ? groupTotal / totalRawSum : undefined);
                  const headerTooltipText =
                    headerTooltipShare === undefined
                      ? valueLabel
                      : `${valueLabel}${VALUE_SHARE_SEPARATOR}${formatShareDetailed(headerTooltipShare)}`;
                  const groupId = String(groupNode.data.id ?? title);
                  const isGroupExpanded =
                    revealOnHover &&
                    hoveredGroupId === groupId &&
                    !detailLoading;
                  const isGroupDimmed =
                    revealOnHover &&
                    hoveredGroupId !== null &&
                    hoveredGroupId !== groupId &&
                    !detailLoading;

                  const fill = resolveColor(title);
                  const headerOnDark = isDarkFill(fill);

                  const handleHeaderClick = () => {
                    onHeaderClick?.(groupId, title, fill);
                  };
                  const handleHeaderKeyDown = (e: KeyboardEvent<Element>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleHeaderClick();
                    }
                  };

                  const headerRect = (
                    <rect
                      data-testid={`treemap-header-${groupId}`}
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill={fill}
                      className={classNames(
                        styles.trayRect,
                        cursorClassName(headerClickable)
                      )}
                      onMouseEnter={
                        revealOnHover
                          ? () => {
                              setHoveredGroupId(groupId);
                              setHoveredLeafId(null);
                            }
                          : undefined
                      }
                      onMouseMove={(event) => {
                        // See the cell handler: re-register the hover on
                        // move so a fast transition can't leave the group
                        // un-expanded. No-op when already current.
                        if (revealOnHover) {
                          setHoveredGroupId(groupId);
                          setHoveredLeafId(null);
                        }
                        showTooltipAtCursor(event, title, headerTooltipText);
                      }}
                      onFocus={(event) =>
                        showTooltipAtElement(
                          event.currentTarget,
                          title,
                          headerTooltipText
                        )
                      }
                      onBlur={hideTooltip}
                      onClick={
                        headerClickable && !detailLoading
                          ? handleHeaderClick
                          : undefined
                      }
                      onKeyDown={
                        headerClickable && !detailLoading
                          ? handleHeaderKeyDown
                          : undefined
                      }
                      role={
                        headerClickable && !detailLoading ? 'button' : undefined
                      }
                      tabIndex={
                        headerClickable && !detailLoading ? 0 : undefined
                      }
                    />
                  );

                  return (
                    <g
                      key={`header-${groupId}-${groupNode.x0}-${groupNode.y0}`}
                      style={
                        {
                          '--replots-dim-color': getDimmedPlotColor(
                            fill,
                            theme,
                            dimIndexByTopNode.get(groupNode)
                          ),
                        } as CSSProperties
                      }
                      className={classNames(
                        styles.fadeableGroup,
                        isGroupDimmed && styles.dimmedGroup
                      )}
                    >
                      {headerRect}
                      {headerLabelShown && (
                        <text
                          x={x + 8}
                          y={trayTop + 16}
                          className={classNames(
                            styles.headerLabel,
                            headerOnDark
                              ? styles.labelOnDarkCell
                              : styles.labelOnLightCell,
                            isGroupExpanded &&
                              hoveredLeafId !== null &&
                              styles.mutedLabel
                          )}
                        >
                          {headerLabel.text}
                        </text>
                      )}
                    </g>
                  );
                })}
                {renderedLeaves.map((leaf) => {
                  const leafYInset = leaf.depth === 1 ? topLevelYInset : 0;
                  const rawW = leaf.x1 - leaf.x0;
                  const rawH = leaf.y1 - leaf.y0 - 2 * leafYInset;
                  const x = Math.max(0, leaf.x0);
                  const y = leaf.y0 + leafYInset;
                  const w = Math.min(width, leaf.x1) - x;
                  const h = Math.max(0, rawH);
                  if (rawW <= 0 || rawH <= 0) return null;

                  let group = leaf;
                  while (group.parent && group.parent.depth > 0) {
                    group = group.parent;
                  }
                  const groupName = group.data.name;
                  const groupId = String(group.data.id ?? groupName);
                  // One box at a time: nested children (depth > 1) render only
                  // while their own group is hovered; top-level leaves always
                  // render.
                  if (
                    revealOnHover &&
                    leaf.depth > 1 &&
                    (hoveredGroupId !== groupId || detailLoading)
                  ) {
                    return null;
                  }
                  const isPreviewCell = revealOnHover && leaf.depth > 1;
                  const isLeafDimmed =
                    revealOnHover &&
                    leaf.depth === 1 &&
                    hoveredGroupId !== null &&
                    hoveredGroupId !== groupId &&
                    !detailLoading;
                  const colorKey = colorByLeaf ? leaf.data.name : groupName;
                  const isOther = isOtherDatum(leaf.data);
                  const isNestedOther = isOther && leaf.depth > 1;
                  const drillsToParent =
                    headerClickable &&
                    (isNestedOther || (isPreviewCell && !clickable));
                  const parentColor = (() => {
                    if (useSingleColor) return singleColor;
                    if (colorByLeaf) return undefined;
                    return colorScale(groupName);
                  })();
                  const fill = (() => {
                    if (leaf.data.color) return leaf.data.color;
                    // Nesting is shown by borders, not shading: children take the
                    // group colour; "Other" keeps the neutral Other colour.
                    if (drillsToParent) {
                      return parentColor ?? resolveColor(groupName);
                    }
                    if (isOther) {
                      return (
                        getOverrideColor('Other', colorOverride) ??
                        resolveColor(String(leaf.data.name ?? ''))
                      );
                    }
                    if (isPreviewCell) {
                      return parentColor ?? resolveColor(colorKey);
                    }
                    return resolveColor(colorKey);
                  })();
                  const labelOnDark = isDarkFill(fill);

                  const name = String(leaf.data.name ?? '');
                  const cellId = String(leaf.data.id ?? name);
                  const isFlatDimmed =
                    flatHover &&
                    hoveredLeafId !== null &&
                    hoveredLeafId !== cellId &&
                    !detailLoading;
                  const displayName = isNestedOther ? NESTED_OTHER_LABEL : name;
                  const parentName = String(
                    leaf.data.parentName ?? leaf.parent?.data.name ?? ''
                  );
                  const displayNumericValue =
                    typeof leaf.data.value !== 'number'
                      ? undefined
                      : toDisplayValue(leaf.data.value);
                  const valueLabel =
                    displayNumericValue === undefined
                      ? '-'
                      : formatValue(displayNumericValue);
                  const shareValue =
                    typeof leaf.data.share === 'number'
                      ? leaf.data.share
                      : undefined;
                  const cellLabel = truncateTextWithFit({
                    text: displayName,
                    maxWidth: Math.max(0, w - LABEL_PADDING_X),
                    // Preview (depth > 1) names render at 10px, committed cells
                    // at 12px — measure at the size actually rendered.
                    fontSize: isPreviewCell ? 10 : 12,
                    fontWeight: isPreviewCell ? 250 : 401,
                  });
                  const showCellName = w > 40 && h >= 16 && cellLabel.fitted;
                  const cellTooltipShare =
                    shareValue ??
                    (totalRawSum > 0 && typeof leaf.data.value === 'number'
                      ? leaf.data.value / totalRawSum
                      : undefined);
                  const cellTooltipText =
                    cellTooltipShare === undefined
                      ? valueLabel
                      : `${valueLabel}${VALUE_SHARE_SEPARATOR}${formatShareDetailed(cellTooltipShare)}`;
                  const cellTipTitle = isNestedOther ? name : displayName;
                  const cellNameY = Math.min(y + 16, y + h - 4);

                  const cellActionable =
                    drillsToParent || (clickable && !isOther);
                  const interactive = cellActionable && !detailLoading;
                  const registerHover = () => {
                    if (flatHover) {
                      setHoveredLeafId(cellId);
                      return;
                    }
                    setHoveredGroupId(groupId);
                    setHoveredLeafId(leaf.depth > 1 ? cellId : null);
                  };
                  const handleClick = () => {
                    if (drillsToParent) {
                      onHeaderClick?.(
                        groupId,
                        groupName,
                        colorByLeaf ? undefined : parentColor
                      );
                      return;
                    }
                    if (clickable) {
                      onCellClick?.(
                        cellId,
                        name,
                        parentName || undefined,
                        fill
                      );
                    }
                  };
                  const handleKeyDown = (e: KeyboardEvent<Element>) => {
                    if (interactive && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleClick();
                    }
                  };

                  const cellRect = (
                    <rect
                      data-testid={`treemap-cell-${cellId}`}
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill={fill}
                      className={classNames(
                        (showCrumbBar && !revealOnHover) ||
                          (revealOnHover && isOther && !isNestedOther)
                          ? styles.trayRect
                          : styles.nodeRect,
                        isPreviewCell && styles.nestedCellBorder,
                        isPreviewCell &&
                          hoveredLeafId === cellId &&
                          styles.nestedCellBorderActive,
                        cursorClassName(cellActionable)
                      )}
                      onMouseEnter={registerHover}
                      onMouseMove={(event) => {
                        // Also drive the highlight here, not just from
                        // onMouseEnter: a fast flick between cells (plus
                        // the paint-order reorder of the active cell)
                        // can swallow the enter, leaving the new cell
                        // un-highlighted. onMouseMove re-registers it and
                        // is a no-op when the value is unchanged.
                        registerHover();
                        showTooltipAtCursor(
                          event,
                          cellTipTitle,
                          cellTooltipText
                        );
                      }}
                      onFocus={(event) =>
                        showTooltipAtElement(
                          event.currentTarget,
                          cellTipTitle,
                          cellTooltipText
                        )
                      }
                      onBlur={hideTooltip}
                      onClick={interactive ? handleClick : undefined}
                      onKeyDown={interactive ? handleKeyDown : undefined}
                      role={interactive ? 'button' : undefined}
                      tabIndex={interactive ? 0 : undefined}
                    />
                  );

                  const mutePreviewLabels =
                    isPreviewCell && hoveredLeafId !== cellId;
                  return (
                    <g
                      key={`${groupId}-${cellId}`}
                      style={
                        {
                          '--replots-dim-color': getDimmedPlotColor(
                            fill,
                            theme,
                            dimIndexByTopNode.get(group)
                          ),
                        } as CSSProperties
                      }
                      className={classNames(
                        styles.fadeableGroup,
                        (isLeafDimmed || isFlatDimmed) && styles.dimmedGroup
                      )}
                    >
                      {cellRect}
                      {showCellName && (
                        <text
                          x={x + 8}
                          y={cellNameY}
                          className={classNames(
                            styles.nodeLabel,
                            isPreviewCell && styles.nodeLabelPreview,
                            labelOnDark
                              ? styles.labelOnDarkCell
                              : styles.labelOnLightCell,
                            mutePreviewLabels && styles.mutedLabel
                          )}
                        >
                          {cellLabel.text}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            )}
          </svg>
        </PlotLoadWrapper>
        {tooltip && (
          <PlotTooltip
            isVisible
            x={tooltip.x}
            y={tooltip.y}
            placement="cursor"
            space="viewport"
          >
            <div className={styles.tooltipTitle}>{tooltip.title}</div>
            <div className={styles.tooltipValue}>{tooltip.valueText}</div>
          </PlotTooltip>
        )}
      </div>
    </div>
  );
};
